import { useState, useCallback, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Search, Download, RefreshCw, ChevronDown, ChevronUp, Monitor,
  Clock, Mic, FileCode, PenLine, Timer, Send, CheckSquare, Eye, User, ArrowLeft,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const EVENT_ICONS = {
  system: Monitor, navigation: Eye, response: PenLine, voice: Mic,
  sandbox: FileCode, timer: Timer, submission: Send, certification: CheckSquare,
  page_view: Eye,
};
const EVENT_COLORS = {
  system: "bg-slate-100 text-slate-700 border-slate-200",
  navigation: "bg-blue-50 text-blue-700 border-blue-200",
  response: "bg-green-50 text-green-700 border-green-200",
  voice: "bg-purple-50 text-purple-700 border-purple-200",
  sandbox: "bg-amber-50 text-amber-700 border-amber-200",
  timer: "bg-orange-50 text-orange-700 border-orange-200",
  submission: "bg-red-50 text-red-700 border-red-200",
  certification: "bg-teal-50 text-teal-700 border-teal-200",
  page_view: "bg-indigo-50 text-indigo-700 border-indigo-200",
};

function fmt(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleString("en-US", {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  });
}

const TYPE_BADGE = { technical_assessment: "TA", hands_on_simulation: "HOS", self_assessment: "SA", manager_validation: "MV" };

export default function ActivityLogTab({ token: authToken }) {
  // ── State: candidate search ──
  const [searchQuery, setSearchQuery] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);

  // ── State: selected candidate ──
  const [selected, setSelected] = useState(null); // { token, name, assessment_type, ... }

  // ── State: activity data ──
  const [data, setData] = useState(null);
  const [responses, setResponses] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState("all");
  const [viewMode, setViewMode] = useState("events");
  const [expandedRows, setExpandedRows] = useState(new Set());

  // ── Search candidates on typing — load all on mount ──
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const loadCandidates = async () => {
      setSearching(true);
      try {
        const q = searchQuery.trim() ? `q=${encodeURIComponent(searchQuery)}&` : "";
        const res = await fetch(`${API}/api/admin/candidates/search?${q}limit=50`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (res.ok) {
          setCandidates(await res.json());
        }
      } catch { /* ignore */ }
      setSearching(false);
    };
    if (!searchQuery.trim()) {
      loadCandidates();
      return;
    }
    debounceRef.current = setTimeout(loadCandidates, 300);
  }, [searchQuery, authToken]);

  // ── Fetch activity log + responses for selected candidate ──
  const fetchLog = useCallback(async (tok) => {
    const t = tok || selected?.token;
    if (!t) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filterType !== "all") params.set("event_type", filterType);
      const [logRes, respRes] = await Promise.all([
        fetch(`${API}/api/admin/activity-log/${t}?${params}`, { headers: { Authorization: `Bearer ${authToken}` } }),
        fetch(`${API}/api/admin/activity-log/${t}/responses`, { headers: { Authorization: `Bearer ${authToken}` } }),
      ]);
      if (!logRes.ok) throw new Error(`HTTP ${logRes.status}`);
      setData(await logRes.json());
      if (respRes.ok) setResponses(await respRes.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [selected, filterType, authToken]);

  // Re-fetch when filter changes
  useEffect(() => { if (selected) fetchLog(); }, [filterType]);

  const selectCandidate = (c) => {
    setSelected(c);
    setSearchQuery("");
    fetchLog(c.token);
  };

  const goBack = () => {
    setSelected(null);
    setData(null);
    setResponses(null);
    setExpandedRows(new Set());
    setFilterType("all");
    setViewMode("events");
  };

  const toggleRow = (id) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const exportJSON = async () => {
    if (!selected) return;
    try {
      const res = await axios.get(`${API}/admin/activity-log/${selected.token}/full-export`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` },
      });
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `THCO-Export-${selected?.name?.replace(/\s+/g, "_") || selected?.token}-${new Date().toISOString().slice(0,10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
      alert("Export failed. Please try again.");
    }
  };

  const events = data?.events || [];
  const sessions = data?.sessions || [];

  // ── No candidate selected: show candidate list ──
  if (!selected) {
    return (
      <div className="space-y-4" data-testid="activity-log-tab">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Filter by candidate name, email, or assessment ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="activity-log-search-input"
          />
          {searching && <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />}
        </div>

        {candidates.length > 0 ? (
          <div className="bg-white border rounded-lg overflow-hidden divide-y max-h-[520px] overflow-y-auto" data-testid="candidate-list">
            {candidates.map((c) => (
              <button
                key={`${c.token}`}
                onClick={() => selectCandidate(c)}
                className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center justify-between gap-4 transition-colors"
                data-testid={`candidate-option-${c.token}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-[#1B3A5C] text-white flex items-center justify-center text-xs font-bold shrink-0">
                    {c.name?.charAt(0) || "?"}
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-sm text-gray-900 truncate">{c.name}</p>
                    <p className="text-xs text-gray-500 truncate">{c.email} &middot; {c.assessment_id}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge variant="outline" className="text-xs">{TYPE_BADGE[c.assessment_type] || c.assessment_type}</Badge>
                  <Badge variant="outline" className={`text-xs ${c.status === "completed" ? "bg-green-50 text-green-700" : c.status === "in_progress" || c.status === "started" ? "bg-blue-50 text-blue-700" : "bg-gray-50 text-gray-600"}`}>
                    {c.status?.replace(/_/g, " ")}
                  </Badge>
                </div>
              </button>
            ))}
          </div>
        ) : !searching ? (
          <div className="text-center py-12 text-gray-400">
            <p className="text-sm">No candidates found{searchQuery ? ` matching "${searchQuery}"` : ""}</p>
          </div>
        ) : null}
      </div>
    );
  }

  // ── Candidate selected: show activity log ──
  return (
    <div className="space-y-5" data-testid="activity-log-detail">
      {/* Header with back button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={goBack} data-testid="activity-log-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="w-10 h-10 rounded-full bg-[#1B3A5C] text-white flex items-center justify-center text-sm font-bold">
            {selected.name?.charAt(0) || "?"}
          </div>
          <div>
            <p className="font-semibold text-gray-900">{selected.name}</p>
            <p className="text-xs text-gray-500">{selected.email} &middot; {selected.assessment_id} &middot; <span className="font-mono">{selected.token?.slice(0, 8)}...</span></p>
          </div>
          <Badge variant="outline" className="ml-2 text-xs">{TYPE_BADGE[selected.assessment_type] || selected.assessment_type}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[160px]" data-testid="activity-log-filter">
              <SelectValue placeholder="All Events" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Events</SelectItem>
              <SelectItem value="system">System</SelectItem>
              <SelectItem value="navigation">Navigation</SelectItem>
              <SelectItem value="response">Responses</SelectItem>
              <SelectItem value="voice">Voice</SelectItem>
              <SelectItem value="sandbox">Sandbox</SelectItem>
              <SelectItem value="timer">Timer</SelectItem>
              <SelectItem value="submission">Submission</SelectItem>
              <SelectItem value="certification">Certification</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => fetchLog()}>
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">{error}</div>}

      {data && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white border rounded-lg p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Total Events</p>
              <p className="text-2xl font-bold text-gray-900" data-testid="activity-total-events">{data.total_events}</p>
            </div>
            <div className="bg-white border rounded-lg p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Sessions</p>
              <p className="text-2xl font-bold text-gray-900">{sessions.length}</p>
            </div>
            <div className="bg-white border rounded-lg p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Responses Saved</p>
              <p className="text-2xl font-bold text-gray-900">{responses?.total_responses || 0}</p>
            </div>
          </div>

          {/* Session metadata */}
          {sessions.length > 0 && (
            <div className="bg-white border rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b bg-gray-50">
                <h3 className="font-semibold text-sm text-gray-700">Session Metadata</h3>
              </div>
              <div className="divide-y">
                {sessions.map((s) => (
                  <div key={s.session_id} className="px-4 py-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                    <span className="text-gray-500">ID: <span className="font-mono text-gray-800">{s.session_id}</span></span>
                    {s.browser && <span className="text-gray-500">Browser: <span className="text-gray-800">{s.browser}</span></span>}
                    {s.os && <span className="text-gray-500">OS: <span className="text-gray-800">{s.os}</span></span>}
                    {s.device_type && <span className="text-gray-500">Device: <span className="text-gray-800">{s.device_type}</span></span>}
                    {s.ip_address && <span className="text-gray-500">IP: <span className="font-mono text-gray-800">{s.ip_address}</span></span>}
                    {s.actions_count != null && <span className="text-gray-500">Actions: <span className="font-bold text-gray-800">{s.actions_count}</span></span>}
                    {s.started_at && <span className="text-gray-500">Started: <span className="text-gray-800">{fmt(s.started_at)}</span></span>}
                    {s.last_activity_at && <span className="text-gray-500">Last: <span className="text-gray-800">{fmt(s.last_activity_at)}</span></span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* View toggle + export */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === "events" ? "default" : "outline"} size="sm"
                onClick={() => setViewMode("events")}
                className={viewMode === "events" ? "bg-[#00AEEF] hover:bg-[#00AEEF]/90" : ""}
                data-testid="view-events-btn"
              >
                <Clock className="w-4 h-4 mr-1" /> Events ({events.length})
              </Button>
              <Button
                variant={viewMode === "responses" ? "default" : "outline"} size="sm"
                onClick={() => setViewMode("responses")}
                className={viewMode === "responses" ? "bg-[#00AEEF] hover:bg-[#00AEEF]/90" : ""}
                data-testid="view-responses-btn"
              >
                <PenLine className="w-4 h-4 mr-1" /> Responses ({responses?.total_responses || 0})
              </Button>
            </div>
            <Button variant="outline" size="sm" onClick={exportJSON}>
              <Download className="w-4 h-4 mr-1" /> Export JSON
            </Button>
          </div>

          {/* ── Responses Table ── */}
          {viewMode === "responses" && (
            <div className="bg-white border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="w-[50px]">#</TableHead>
                    <TableHead className="w-[200px]">Question</TableHead>
                    <TableHead>Response Content</TableHead>
                    <TableHead className="w-[280px]">Changes Only</TableHead>
                    <TableHead className="w-[80px]">Mode</TableHead>
                    <TableHead className="w-[70px]">Typed</TableHead>
                    <TableHead className="w-[70px]">Pasted</TableHead>
                    <TableHead className="w-[120px]">Updated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(responses?.responses || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12 text-gray-400">
                        No responses saved yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    (responses.responses || [])
                    .filter((r) => {
                      // Filter out empty/duplicate HOS entries
                      const text = typeof r.response === "string" ? r.response : JSON.stringify(r.response);
                      if (r.question_id?.includes("browser_sim") && (!text || text.trim() === "" || text === '""')) return false;
                      return true;
                    })
                    .map((r, idx) => {
                      const text = typeof r.response === "string" ? r.response : JSON.stringify(r.response);
                      const isCode = r.question_id?.includes("__file__");
                      const isLong = text && text.length > 120;
                      const isExpanded = expandedRows.has(`resp-${idx}`);
                      // Parse clean file name from HOS question_id
                      const fileName = r.question_id?.includes("__file__")
                        ? r.question_id.split("__file__")[1]?.split("_browser_sim")[0] || r.question_id
                        : null;
                      return (
                        <TableRow key={idx} className="hover:bg-gray-50 cursor-pointer align-top" onClick={() => isLong && toggleRow(`resp-${idx}`)}>
                          <TableCell className="font-bold text-sm text-gray-700">{r.question_number || "-"}</TableCell>
                          <TableCell>
                            {fileName ? (
                              <>
                                <p className="font-mono text-sm font-semibold text-emerald-700">{fileName}</p>
                                <Badge variant="outline" className="text-[10px] mt-1 bg-emerald-50 border-emerald-200 text-emerald-600">file edit</Badge>
                              </>
                            ) : (
                              <>
                                <p className="font-medium text-sm text-gray-800 line-clamp-1">{r.question_title || r.question_id}</p>
                                {r.field_label && <p className="text-xs text-[#00AEEF] font-medium mt-0.5">{r.field_label}</p>}
                                {r.question_text && <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">{r.question_text}</p>}
                                {r.question_sub_type && <Badge variant="outline" className="text-[10px] mt-1">{r.question_sub_type}</Badge>}
                              </>
                            )}
                            <p className="text-[10px] text-gray-400 mt-0.5 font-mono">{r.section_name}</p>
                          </TableCell>
                          <TableCell className="text-sm text-gray-800">
                            {isCode ? (
                              <pre className={`font-mono text-xs bg-gray-900 text-green-300 p-2 rounded-md overflow-auto whitespace-pre-wrap ${isExpanded ? "max-h-[500px]" : "max-h-[80px]"}`}>
                                {text || <span className="text-gray-500 italic">no changes</span>}
                              </pre>
                            ) : (
                              <div className={isExpanded ? "" : "line-clamp-2"}>
                                {text || <span className="text-gray-300 italic">empty</span>}
                              </div>
                            )}
                            {isLong && (
                              <button className="text-[10px] text-blue-500 mt-1 hover:underline" onClick={(e) => { e.stopPropagation(); toggleRow(`resp-${idx}`); }}>
                                {isExpanded ? "collapse" : "expand full content"}
                              </button>
                            )}
                            {r.voice_audio_url && (
                              <div className="mt-1 flex items-center gap-2">
                                <a href={r.voice_audio_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center">
                                  <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100 cursor-pointer">
                                    <Mic className="w-3 h-3 mr-1" /> {r.voice_duration_seconds || 0}s audio — click to play
                                  </Badge>
                                </a>
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">
                            {r.changes_only ? (
                              <pre className={`font-mono text-[11px] p-2 rounded-md overflow-auto whitespace-pre-wrap bg-gray-50 border ${expandedRows.has(`diff-${idx}`) ? "max-h-[500px]" : "max-h-[100px]"}`}>
                                {r.changes_only.split("\n").map((line, li) => (
                                  <span key={li} className={line.startsWith("+ ") ? "text-green-700 bg-green-50 block" : line.startsWith("- ") ? "text-red-600 bg-red-50 block line-through opacity-70" : "block"}>
                                    {line}
                                  </span>
                                ))}
                              </pre>
                            ) : (
                              <span className="text-gray-300 italic text-xs">{isCode ? "no changes" : "—"}</span>
                            )}
                            {r.changes_only && r.changes_only.length > 300 && (
                              <button className="text-[10px] text-blue-500 mt-1 hover:underline" onClick={(e) => { e.stopPropagation(); toggleRow(`diff-${idx}`); }}>
                                {expandedRows.has(`diff-${idx}`) ? "collapse" : "expand diff"}
                              </button>
                            )}
                          </TableCell>
                          <TableCell><Badge variant="outline" className="text-xs">{r.response_mode || "typed"}</Badge></TableCell>
                          <TableCell className="text-xs text-gray-600 text-center">{r.typed_chars ?? "-"}</TableCell>
                          <TableCell className="text-xs text-gray-600 text-center">{r.pasted_chars ?? "-"}</TableCell>
                          <TableCell className="font-mono text-xs text-gray-500">{fmt(r.updated_at)}</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          )}

          {/* ── Events Table ── */}
          {viewMode === "events" && (
            <div className="bg-white border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="w-10"></TableHead>
                    <TableHead className="w-[160px]">Timestamp</TableHead>
                    <TableHead className="w-[120px]">Type</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead className="w-[140px]">Section</TableHead>
                    <TableHead className="w-[140px]">Question</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-12 text-gray-400">
                        No events found{filterType !== "all" ? ` with type "${filterType}"` : ""}.
                      </TableCell>
                    </TableRow>
                  ) : (
                    events.map((evt) => {
                      const Icon = EVENT_ICONS[evt.event_type] || Clock;
                      const colorClass = EVENT_COLORS[evt.event_type] || EVENT_COLORS.system;
                      const isExpanded = expandedRows.has(evt.id);
                      const hasDetail = evt.detail && Object.keys(evt.detail).length > 0;
                      return (
                        <>
                          <TableRow
                            key={evt.id}
                            className={`cursor-pointer hover:bg-gray-50 transition-colors ${isExpanded ? "bg-gray-50/50" : ""}`}
                            onClick={() => hasDetail && toggleRow(evt.id)}
                          >
                            <TableCell className="text-center">
                              {hasDetail ? (isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400 mx-auto" /> : <ChevronDown className="w-4 h-4 text-gray-400 mx-auto" />) : null}
                            </TableCell>
                            <TableCell className="font-mono text-xs text-gray-600 whitespace-nowrap">{fmt(evt.timestamp)}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className={`text-xs gap-1 ${colorClass}`}>
                                <Icon className="w-3 h-3" /> {evt.event_type}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-medium text-sm text-gray-800">{evt.event_name?.replace(/_/g, " ")}</TableCell>
                            <TableCell className="font-mono text-xs text-gray-500 truncate max-w-[140px]">{evt.section_id || "-"}</TableCell>
                            <TableCell className="font-mono text-xs text-gray-500 truncate max-w-[140px]">{evt.question_id || "-"}</TableCell>
                          </TableRow>
                          {isExpanded && hasDetail && (
                            <TableRow key={`detail-${evt.id}`}>
                              <TableCell colSpan={6} className="bg-gray-50 px-6 py-3">
                                <pre className="text-xs bg-white rounded border p-3 overflow-x-auto text-gray-700 max-h-40">
                                  {JSON.stringify(evt.detail, null, 2)}
                                </pre>
                              </TableCell>
                            </TableRow>
                          )}
                        </>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}

      {loading && !data && (
        <div className="text-center py-16 text-gray-400">
          <RefreshCw className="w-8 h-8 mx-auto mb-2 animate-spin" />
          <p>Loading activity log...</p>
        </div>
      )}
    </div>
  );
}
